import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar";
import Header from "../Header";
import "./tpl.css";
import "./API.css";
import API_BASE_URL from "../../config";
import axios from "axios";
import ConfirmDialog from "../../components/ConfirmDialog";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import { notify } from "../../components/Notify";

const createBlankWebhook = (prefill) => ({
  id: prefill?.id || `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  url: "",
  devices: [],
  channels: ["sms"],
  wa_numbers: [],
  im_handles: [],
  ...prefill,
});

const mapApiWebhook = (hook = {}) => {
  let devices = [];
  if (Array.isArray(hook.devices)) {
    devices = hook.devices.map((d) => {
      if (typeof d === "object" && d !== null) return { id: d.id, sims: Array.isArray(d.sims) ? d.sims : [] };
      return { id: String(d), sims: Array.isArray(hook.sims) ? hook.sims : [] };
    });
  }
  const channels = Array.isArray(hook.channels) && hook.channels.length ? hook.channels : ["sms"];
  const wa_numbers = Array.isArray(hook.wa_numbers) ? hook.wa_numbers : [];
  const im_handles = Array.isArray(hook.im_handles) ? hook.im_handles : [];
  return createBlankWebhook({ id: hook.id, url: hook.url || "", devices, channels, wa_numbers, im_handles });
};

function getDeviceSims(selectedSim) {
  const sel = (selectedSim || "SIM1").toUpperCase();
  if (sel === "BOTH") return ["SIM1", "SIM2"];
  return [sel];
}

export default function APIKey() {
  const [token, setToken] = useState("");
  const maskedToken =
    token.length > 8
      ? `${token.slice(0, 4)}${"•".repeat(24)}${token.slice(-4)}`
      : "";
  const [showPopup, setShowPopup] = useState(false);
  const [devices, setDevices] = useState([]);
  const [waConnected, setWaConnected] = useState(false);
  const [waNumbers, setWaNumbers] = useState([]);
  const [imConnected, setImConnected] = useState(false);
  const [imHandles, setImHandles] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [signingSecret, setSigningSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [secretToast, setSecretToast] = useState("");
  const [saving, setSaving] = useState(false);
  const [webhookToast, setWebhookToast] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(createBlankWebhook());
  const [editingIndex, setEditingIndex] = useState(null);
  const [apiKeyToast, setApiKeyToast] = useState("");
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const { confirmProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    const loadApiKey = async () => {
      try {
        const authToken = localStorage.getItem("userToken");
        if (!authToken) return;
        const headers = { Authorization: `Bearer ${authToken}` };
        const { data } = await axios.get(`${API_BASE_URL}/auth/api-key`, { headers });
        if (data?.token) setToken(data.token);
      } catch (err) {
        console.error("Failed to load API key", err.response?.data || err.message);
      }
    };

    const loadWebhookConfig = async () => {
      try {
        const authToken = localStorage.getItem("userToken");
        if (!authToken) return;
        const headers = { Authorization: `Bearer ${authToken}` };
        const [configRes, deviceRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/webhooks/inbound-sms`, { headers }),
          axios.get(`${API_BASE_URL}/dashboard/devices`, { headers }),
        ]);

        setDevices(deviceRes.data || []);
        const apiWebhooks = Array.isArray(configRes.data?.webhooks)
          ? configRes.data.webhooks.map(mapApiWebhook)
          : [];
        setWebhooks(apiWebhooks.length ? apiWebhooks : []);
        setSigningSecret(configRes.data?.signingSecret || "");
      } catch (err) {
        console.error("Failed to load webhook config", err.response?.data || err.message);
      }
    };

    const loadWhatsApp = async () => {
      try {
        const authToken = localStorage.getItem("userToken");
        if (!authToken) return;
        const headers = { Authorization: `Bearer ${authToken}` };
        const { data } = await axios.get(`${API_BASE_URL}/whatsapp/numbers`, { headers });
        const connected = (data?.numbers || []).filter((n) => n.status === "connected");
        setWaNumbers(connected);
        setWaConnected(connected.length > 0);
      } catch (err) {
        // 402 (not subscribed) or any error → WhatsApp option stays hidden
        setWaNumbers([]);
        setWaConnected(false);
      }
    };

    const loadIMessage = async () => {
      try {
        const authToken = localStorage.getItem("userToken");
        if (!authToken) return;
        const headers = { Authorization: `Bearer ${authToken}` };
        const { data } = await axios.get(`${API_BASE_URL}/imessage/handles`, { headers });
        const handles = data?.handles || [];
        setImHandles(handles);
        setImConnected(handles.length > 0);
      } catch (err) {
        setImHandles([]);
        setImConnected(false);
      }
    };

    loadApiKey();
    loadWebhookConfig();
    loadWhatsApp();
    loadIMessage();
  }, []);

  const openModalForNew = () => {
    setEditingIndex(null);
    setModalData(createBlankWebhook());
    setModalOpen(true);
  };

  const openModalForEdit = (index) => {
    setEditingIndex(index);
    setModalData(createBlankWebhook(webhooks[index]));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalData(createBlankWebhook());
    setEditingIndex(null);
  };

  const isDeviceSelected = (deviceId) => modalData.devices.some((d) => d.id === deviceId);

  const getDeviceSimsInModal = (deviceId) => {
    const entry = modalData.devices.find((d) => d.id === deviceId);
    return entry?.sims || [];
  };

  const toggleDevice = (deviceId, availableSims) => {
    setModalData((prev) => {
      const exists = prev.devices.find((d) => d.id === deviceId);
      if (exists) {
        return { ...prev, devices: prev.devices.filter((d) => d.id !== deviceId) };
      }
      return { ...prev, devices: [...prev.devices, { id: deviceId, sims: [...availableSims] }] };
    });
  };

  const toggleDeviceSim = (deviceId, sim) => {
    setModalData((prev) => ({
      ...prev,
      devices: prev.devices.map((d) => {
        if (d.id !== deviceId) return d;
        const hasSim = d.sims.includes(sim);
        const newSims = hasSim ? d.sims.filter((s) => s !== sim) : [...d.sims, sim];
        return { ...d, sims: newSims.length ? newSims : [sim] };
      }),
    }));
  };

  const hasChannel = (ch) => (modalData.channels || []).includes(ch);

  const toggleChannel = (ch) => {
    setModalData((prev) => {
      const set = new Set(prev.channels || ["sms"]);
      if (set.has(ch)) set.delete(ch);
      else set.add(ch);
      if (set.size === 0) set.add("sms"); // never allow an empty channel set
      return { ...prev, channels: Array.from(set) };
    });
  };

  const isWaNumberSelected = (id) => (modalData.wa_numbers || []).includes(id);

  const toggleWaNumber = (id) => {
    setModalData((prev) => {
      const set = new Set(prev.wa_numbers || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, wa_numbers: Array.from(set) };
    });
  };

  const isImHandleSelected = (id) => (modalData.im_handles || []).includes(id);

  const toggleImHandle = (id) => {
    setModalData((prev) => {
      const set = new Set(prev.im_handles || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, im_handles: Array.from(set) };
    });
  };

  const handleModalSave = () => {
    const trimmedUrl = modalData.url.trim();
    if (!trimmedUrl) return;
    try { new URL(trimmedUrl); } catch {
      notify("Please enter a valid URL (e.g. https://example.com/webhook).", "error");
      return;
    }
    const normalized = { ...modalData, url: trimmedUrl };
    setWebhooks((prev) => {
      if (editingIndex === null) {
        return [...prev, createBlankWebhook(normalized)];
      }
      const next = [...prev];
      next[editingIndex] = createBlankWebhook({
        ...normalized,
        id: prev[editingIndex].id || normalized.id,
      });
      return next;
    });
    closeModal();
  };

  const removeWebhookEntry = (index) => {
    setWebhooks((prev) => prev.filter((_, i) => i !== index));
  };

  const requestWebhookDelete = (index) => {
    const target = webhooks[index];
    if (!target) return;
    const message = (
      <p>
        Are you sure you want to remove <strong>{target.url || "this webhook"}</strong>? SMS traffic will stop
        forwarding immediately.
      </p>
    );
    openConfirm({
      title: "Delete Webhook",
      message,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: () => removeWebhookEntry(index),
    });
  };

  const handleWebhookSave = async (e) => {
    e.preventDefault();
    const sanitized = webhooks
      .map((hook) => ({ ...hook, url: hook.url.trim() }))
      .filter((hook) => hook.url.length);

    if (!sanitized.length) {
      setWebhookToast("Add at least one webhook URL.");
      setTimeout(() => setWebhookToast(""), 2200);
      return;
    }

    try {
      setSaving(true);
      const authToken = localStorage.getItem("userToken");
      const headers = { Authorization: `Bearer ${authToken}` };
      const { data } = await axios.put(
        `${API_BASE_URL}/webhooks/inbound-sms`,
        { webhooks: sanitized },
        { headers }
      );
      const savedHooks = Array.isArray(data?.webhooks) ? data.webhooks.map(mapApiWebhook) : [];
      setWebhooks(savedHooks.length ? savedHooks : []);
      setWebhookToast(data?.message || "Webhook settings saved");
    } catch (err) {
      console.error("Failed to save webhook", err.response?.data || err.message);
      setWebhookToast(err.response?.data?.error || "Unable to save webhook");
    } finally {
      setSaving(false);
      setTimeout(() => setWebhookToast(""), 2200);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(token);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 2000);
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(signingSecret);
    setSecretToast("Signing secret copied");
    setTimeout(() => setSecretToast(""), 2000);
  };

  const showApiKeyStatus = (message) => {
    setApiKeyToast(message);
    setTimeout(() => setApiKeyToast(""), 2400);
  };

  const regenerateApiKey = async () => {
    const authToken = localStorage.getItem("userToken");
    if (!authToken) {
      showApiKeyStatus("Please sign in again to rotate your key.");
      return;
    }

    try {
      setRegeneratingKey(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/api-key/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!data?.token) {
        throw new Error("Token missing from response");
      }
      setToken(data.token);
      showApiKeyStatus("API key regenerated");
    } catch (err) {
      console.error("Failed to regenerate API key", err.response?.data || err.message);
      showApiKeyStatus(err.response?.data?.error || "Unable to regenerate API key");
    } finally {
      setRegeneratingKey(false);
    }
  };

  const requestRegenerateApiKey = () => {
    openConfirm({
      title: "Regenerate API Key",
      message: (
        <p>
          This will immediately replace the current key and revoke the old one. API keys do not expire automatically, so
          rotate only when needed. Copy the new token into any apps, SDKs, or automations using the Pairfon API to avoid
          authentication failures.
        </p>
      ),
      confirmLabel: "Regenerate",
      onConfirm: regenerateApiKey,
    });
  };

  const webhookHasSms = (hook) => (hook.channels || ["sms"]).includes("sms");

  const buildDeviceSummary = (hook) => {
    if (!hook.devices.length) return "All devices";
    return hook.devices.map((d) => {
      const match = devices.find((dev) => dev.device_id === d.id);
      const name = match?.device_name || d.id.slice(0, 8) + "...";
      const simLabel = d.sims.length ? d.sims.join("+") : "All SIMs";
      return `${name} (${simLabel})`;
    }).join(", ");
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4 api-page">
          <div className="page-header dash-head">
            <div>
              <h4>API &amp; Webhooks</h4>
              <p className="page-subtitle">Programmatic access for your own integrations.</p>
            </div>
            <a className="tpl-btn ghost" href="/api-docs" target="_blank" rel="noopener noreferrer" style={{ flex: "0 0 auto" }}>
              View API docs
            </a>
          </div>

          <div className="tpl-grid2">
            <div className="tpl-card">
              <div className="tpl-card-h">
                <h6>API key</h6>
                {apiKeyToast && <span className="tpl-tag info">{apiKeyToast}</span>}
              </div>
              <div className="tpl-field">
                <label>Live key</label>
                <div className="api-key-box">
                  <span className="masked-key">{maskedToken || "No key generated yet"}</span>
                  {token && (
                    <i className="bi bi-clipboard copy-icon" title="Copy API key" onClick={handleCopy}></i>
                  )}
                </div>
              </div>
              <div className="tpl-btnrow" style={{ marginTop: 14 }}>
                <button type="button" className="tpl-btn ghost" onClick={requestRegenerateApiKey} disabled={regeneratingKey}>
                  {regeneratingKey ? "Regenerating…" : "Regenerate key"}
                </button>
              </div>
              <p className="tpl-mut" style={{ fontSize: "0.8rem", margin: "12px 0 0" }}>
                Rotate the key if it was shared or compromised. Existing integrations must use the new value immediately.
              </p>
            </div>

            <div className="tpl-card">
              <div className="tpl-card-h">
                <h6>Authentication</h6>
                <a className="lnk" href="/api-docs" target="_blank" rel="noopener noreferrer">API docs →</a>
              </div>
              <p className="tpl-mut" style={{ fontSize: "0.85rem", margin: "0 0 10px" }}>
                Include your key as a Bearer token in the request header:
              </p>
              <code className="api-code">Authorization: Bearer sk-xxxx-xxxx-xxxx-xxxx</code>
              <p className="tpl-mut" style={{ fontSize: "0.8rem", margin: "12px 0 0" }}>
                Keep your key secret. Keys don't expire automatically — regenerating immediately invalidates the previous one.
              </p>
            </div>
          </div>

          <div className="tpl-card">
            <div className="tpl-card-h">
              <div>
                <h6>Inbound webhooks</h6>
                <span className="tpl-mut" style={{ fontSize: "0.8rem" }}>
                  A POST fires each time the selected devices, numbers or handles receive a message.
                </span>
              </div>
              <div className="webhook-heading-actions">
                {webhookToast && <span className="tpl-tag info">{webhookToast}</span>}
                <button type="button" className="tpl-btn ghost" onClick={openModalForNew}>
                  ＋ Add webhook
                </button>
              </div>
            </div>

            <form onSubmit={handleWebhookSave} className="webhook-form">
              <div className="webhook-stack">
                {webhooks.length ? (
                  webhooks.map((hook, index) => (
                    <div className="webhook-card summary" key={hook.id || index}>
                      <div className="webhook-card-header">
                        <div>
                          <strong>{hook.url || "Untitled webhook"}</strong>
                          <p className="text-muted small mb-1">{buildDeviceSummary(hook)}</p>
                          <div className="webhook-chip-row">
                            {(hook.channels || ["sms"]).map((ch) => (
                              <span className={`webhook-chip channel ${ch}`} key={`ch-${ch}`}>
                                {ch === "whatsapp" ? "WhatsApp" : ch === "imessage" ? "iMessage" : "SMS"}
                              </span>
                            ))}
                            {webhookHasSms(hook) && hook.devices.slice(0, 4).map((d) => {
                              const match = devices.find((dev) => dev.device_id === d.id);
                              const name = match?.device_name || d.id.slice(0, 8) + "...";
                              const simLabel = d.sims.length ? d.sims.join("+") : "All";
                              return (
                                <span className="webhook-chip" key={d.id}>
                                  {name} &middot; {simLabel}
                                </span>
                              );
                            })}
                            {webhookHasSms(hook) && !hook.devices.length && (
                              <span className="webhook-chip">All devices &middot; All SIMs</span>
                            )}
                            {webhookHasSms(hook) && hook.devices.length > 4 && (
                              <span className="webhook-chip">+{hook.devices.length - 4} more</span>
                            )}
                            {(hook.channels || ["sms"]).includes("whatsapp") && (
                              (hook.wa_numbers && hook.wa_numbers.length)
                                ? hook.wa_numbers.slice(0, 3).map((id) => {
                                    const m = waNumbers.find((n) => n.id === id);
                                    return (
                                      <span className="webhook-chip" key={`wa-${id}`}>
                                        🟢 {m ? (m.label || m.phone) : "WhatsApp"}
                                      </span>
                                    );
                                  })
                                : <span className="webhook-chip">🟢 All WhatsApp numbers</span>
                            )}
                            {(hook.channels || ["sms"]).includes("imessage") && (
                              (hook.im_handles && hook.im_handles.length)
                                ? hook.im_handles.slice(0, 3).map((id) => {
                                    const h = imHandles.find((x) => x.id === id);
                                    return (
                                      <span className="webhook-chip" key={`im-${id}`}>
                                        💬 {h ? (h.label || h.handle) : "iMessage"}
                                      </span>
                                    );
                                  })
                                : <span className="webhook-chip">💬 All iMessage handles</span>
                            )}
                          </div>
                        </div>
                        <div className="webhook-card-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => openModalForEdit(index)}
                            title="Edit webhook"
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() => requestWebhookDelete(index)}
                            title="Delete webhook"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="tpl-mut" style={{ margin: 0 }}>No webhooks yet — click <b>＋ Add webhook</b> to get started.</p>
                )}
              </div>

              <div className="webhook-actions">
                <button type="submit" className="tpl-btn" disabled={saving || !webhooks.length}>
                  {saving ? "Saving…" : "Save webhooks"}
                </button>
                <span className="tpl-mut" style={{ fontSize: "0.8rem" }}>
                  All active webhooks fire simultaneously; use filters to route only the traffic you need.
                </span>
              </div>
            </form>
          </div>

          {signingSecret && (
            <div className="tpl-card">
              <div className="tpl-card-h">
                <h6>Webhook signing <span className="tpl-tag off" style={{ marginLeft: 8 }}>Optional · advanced</span></h6>
              </div>
              <p className="tpl-mut" style={{ fontSize: "0.85rem", margin: "0 0 14px" }}>
                Every webhook delivery includes an <code className="api-code">X-Pairfon-Signature</code> header so you can
                confirm it really came from Pairfon. This is <b>optional</b> — your webhooks work the same whether or not
                you verify. You can skip it entirely if you use Zapier, Make, or GoHighLevel.
              </p>
              <div className="tpl-field">
                <label>Signing secret</label>
                <div className="api-key-box">
                  <span className="masked-key">{showSecret ? signingSecret : "whsec_••••••••••••••••••••••••"}</span>
                  <i
                    className={`bi ${showSecret ? "bi-eye-slash" : "bi-eye"} copy-icon`}
                    title={showSecret ? "Hide" : "Reveal"}
                    onClick={() => setShowSecret((s) => !s)}
                    style={{ marginRight: 10 }}
                  ></i>
                  <i className="bi bi-clipboard copy-icon" title="Copy signing secret" onClick={handleCopySecret}></i>
                </div>
              </div>
              {secretToast && (
                <p className="tpl-mut" style={{ fontSize: "0.8rem", margin: "8px 0 0", color: "var(--pf-success)" }}>{secretToast}</p>
              )}
              <details style={{ marginTop: 14 }}>
                <summary className="tpl-mut" style={{ cursor: "pointer", fontSize: "0.85rem" }}>How to verify (for developers)</summary>
                <code className="api-code" style={{ display: "block", whiteSpace: "pre", overflowX: "auto", marginTop: 8, padding: "12px 14px" }}>{`sig = header "X-Pairfon-Signature"  -> strip "sha256="
ts  = header "X-Pairfon-Timestamp"
expected = HMAC_SHA256(signingSecret, ts + "." + rawBody)
valid = (sig === expected) && (now - ts < 300 seconds)`}</code>
              </details>
            </div>
          )}
        </div>

        {showPopup && <div className="copy-popup">API Key copied to clipboard</div>}
        {modalOpen && (
          <div className="webhook-modal">
            <div className="webhook-modal-content">
              <div className="webhook-modal-header">
                <h6>{editingIndex === null ? "Add Webhook" : "Edit Webhook"}</h6>
                <button type="button" className="icon-btn" onClick={closeModal} title="Close">
                  <i className="bi bi-x-lg" />
                </button>
              </div>
              <div className="webhook-modal-body">
                <label className="form-label">Webhook URL</label>
                <input
                  type="url"
                  className="form-control"
                  placeholder="https://example.com/hooks/inbound-sms"
                  value={modalData.url}
                  onChange={(e) => setModalData((prev) => ({ ...prev, url: e.target.value }))}
                  required
                />
                <small className="text-muted d-block mb-2">
                  Pairfon posts JSON payloads containing message, sender, channel, device fingerprint, SIM slot, and timestamps.
                </small>

                <label className="form-label">Channels</label>
                <small className="text-muted d-block mb-2">
                  Choose which message types this webhook receives.
                </small>
                <div className="webhook-channel-row">
                  <label className={`webhook-channel-opt${hasChannel("sms") ? " active" : ""}`}>
                    <input type="checkbox" checked={hasChannel("sms")} onChange={() => toggleChannel("sms")} />
                    <i className="bi bi-phone" /> SMS
                  </label>
                  {waConnected && (
                    <label className={`webhook-channel-opt${hasChannel("whatsapp") ? " active wa" : ""}`}>
                      <input type="checkbox" checked={hasChannel("whatsapp")} onChange={() => toggleChannel("whatsapp")} />
                      <i className="bi bi-whatsapp" /> WhatsApp
                    </label>
                  )}
                  {imConnected && (
                    <label className={`webhook-channel-opt${hasChannel("imessage") ? " active im" : ""}`}>
                      <input type="checkbox" checked={hasChannel("imessage")} onChange={() => toggleChannel("imessage")} />
                      <i className="bi bi-chat-fill" /> iMessage
                    </label>
                  )}
                </div>

                {hasChannel("whatsapp") && (
                <>
                <label className="form-label">WhatsApp Numbers</label>
                <small className="text-muted d-block mb-2">
                  Select which connected numbers trigger this webhook. Leave empty to forward from all.
                </small>
                <div className="device-filter-list">
                  {waNumbers.length === 0 ? (
                    <p className="text-muted small">No connected WhatsApp numbers.</p>
                  ) : (
                    waNumbers.map((n) => {
                      const selected = isWaNumberSelected(n.id);
                      return (
                        <div key={n.id} className={`device-filter-row${selected ? " selected" : ""}`}>
                          <label className="device-filter-check">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleWaNumber(n.id)}
                            />
                            <div className="device-filter-info">
                              <span className="device-filter-name">
                                {n.label || "WhatsApp"}{n.phone ? ` (${n.phone})` : ""}
                              </span>
                            </div>
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
                </>
                )}

                {hasChannel("imessage") && (
                <>
                <label className="form-label">iMessage Handles</label>
                <small className="text-muted d-block mb-2">
                  Select which Macs/handles trigger this webhook. Leave empty to forward from all.
                </small>
                <div className="device-filter-list">
                  {imHandles.length === 0 ? (
                    <p className="text-muted small">No connected iMessage handles.</p>
                  ) : (
                    imHandles.map((h) => {
                      const selected = isImHandleSelected(h.id);
                      return (
                        <div key={h.id} className={`device-filter-row${selected ? " selected" : ""}`}>
                          <label className="device-filter-check">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleImHandle(h.id)}
                            />
                            <div className="device-filter-info">
                              <span className="device-filter-name">
                                {h.label || "Mac"}{h.handle ? ` (${h.handle})` : ""}
                              </span>
                            </div>
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
                </>
                )}

                {hasChannel("sms") && (
                <>
                <label className="form-label">Devices & SIM Filters</label>
                <small className="text-muted d-block mb-2">
                  Select which devices and SIM slots trigger this webhook. Leave empty to forward from all devices.
                </small>

                <div className="device-filter-list">
                  {devices.length === 0 ? (
                    <p className="text-muted small">No devices found. Pair a device first.</p>
                  ) : (
                    devices.map((device) => {
                      const availableSims = getDeviceSims(device.selected_sim);
                      const selected = isDeviceSelected(device.device_id);
                      const selectedSims = getDeviceSimsInModal(device.device_id);
                      const isOnline = device.status === "active";

                      return (
                        <div
                          key={device.device_id}
                          className={`device-filter-row${selected ? " selected" : ""}`}
                        >
                          <label className="device-filter-check">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleDevice(device.device_id, availableSims)}
                            />
                            <div className="device-filter-info">
                              <span className="device-filter-name">
                                {device.device_name || device.device_id.slice(0, 12) + "..."}
                              </span>
                              <span className={`device-filter-status ${isOnline ? "online" : "offline"}`}>
                                {isOnline ? "Online" : "Offline"}
                              </span>
                            </div>
                          </label>

                          {selected && (
                            <div className="device-sim-toggles">
                              {availableSims.map((sim) => (
                                <label
                                  key={sim}
                                  className={`sim-toggle${selectedSims.includes(sim) ? " active" : ""}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedSims.includes(sim)}
                                    onChange={() => toggleDeviceSim(device.device_id, sim)}
                                  />
                                  {sim.replace("SIM", "SIM ")}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                </>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="pf-btn pf-btn-outline" onClick={closeModal}>
                  Cancel
                </button>
                <button type="button" className="pf-btn pf-btn-primary" onClick={handleModalSave}>
                  {editingIndex === null ? "Add Webhook" : "Update Webhook"}
                </button>
              </div>
            </div>
          </div>
        )}
        <ConfirmDialog {...confirmProps} />
      </div>
    </div>
  );
}
